import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { 
  Search, Upload, Trash2, FileText, Home, ChevronRight, 
  FolderOpen, Calendar, Clock, Receipt, AlertCircle,
  CheckCircle, Timer, Download, Eye, X, Package
} from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";

interface BidItem {
  id: number;
  order_id: string;
  project_no: string;
  project_name: string;
  order_amount: number;
  file_get_start_time: string;
  file_get_end_time: string;
  open_bid_time: string;
  actual_open_bid_time?: string;
  bidding_url: string | null;
  invoice_url: string | null;
  expressnumber: string | null;
  invoice_request_time: string | null;
  announcement_type?: number;
  allow_e_bid?: number;
  quote_amount?: string | number;
  quote_amount_upper?: string;
  content_pdf?: string | null;
}

export default function MyBidsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [bids, setBids] = useState<BidItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  const [showInvoiceConfirm, setShowInvoiceConfirm] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showExpressModal, setShowExpressModal] = useState(false);
  const [currentExpressNumber, setCurrentExpressNumber] = useState("");
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [announcementType, setAnnouncementType] = useState<number>(1); // 1=我的投标, 4=其他公告报名
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBidForUpload, setSelectedBidForUpload] = useState<BidItem | null>(null);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // 获取token
  const getToken = () => localStorage.getItem('token');

  // 显示 Toast 提示
  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 2000);
  };

  // 从API获取数据
  const fetchBids = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`/api/trade-orders/my?keyword=${encodeURIComponent(searchKeyword)}&announcement_type=${announcementType}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.ok) {
        setBids(result.data || []);
      } else {
        console.error('获取数据失败:', result.error);
      }
    } catch (error) {
      console.error('请求失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBids();
  }, [announcementType]);

  // 搜索时重新获取数据
  const handleSearch = () => {
    fetchBids();
  };

  // 过滤后的数据（前端搜索）
  const filteredBids = bids.filter((bid) =>
    bid.project_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    bid.project_no?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  // 格式化金额
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: "CNY",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // 格式化日期时间（精确到时分秒，东八区时间）
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    
    try {
      // 解析日期字符串
      const date = new Date(dateStr);
      
      // 如果日期无效，返回 '-'
      if (isNaN(date.getTime())) return '-';
      
      // 使用 Intl.DateTimeFormat 转换为东八区时间（Asia/Shanghai）
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      // 格式化日期时间
      const parts = formatter.formatToParts(date);
      const year = parts.find(p => p.type === 'year')?.value;
      const month = parts.find(p => p.type === 'month')?.value;
      const day = parts.find(p => p.type === 'day')?.value;
      const hours = parts.find(p => p.type === 'hour')?.value;
      const minutes = parts.find(p => p.type === 'minute')?.value;
      const seconds = parts.find(p => p.type === 'second')?.value;
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('日期格式化错误:', error);
      return '-';
    }
  };

  // 检查是否可以上传（开标时间之前，且allow_e_bid=1）
  const canUpload = (openBidTime: string | null, allowEBid: number | null | undefined, actualOpenBidTime?: string | null) => {
    // 优先使用actual_open_bid_time（变更公告的开标时间）
    const effectiveOpenBidTime = actualOpenBidTime || openBidTime;
    
    // 如果是其他公告报名（announcement_type=4），不检查allow_e_bid
    if (announcementType === 4) {
      if (!effectiveOpenBidTime) return true;
      const now = new Date();
      const openBidDate = new Date(effectiveOpenBidTime);
      return now < openBidDate;
    }
    
    // 如果是我的投标（announcement_type=1），需要检查allow_e_bid
    if (allowEBid === 0) {
      return false; // 不支持电子投标
    }
    
    if (!effectiveOpenBidTime) return true;
    const now = new Date();
    const openBidDate = new Date(effectiveOpenBidTime);
    return now < openBidDate;
  };

  // 获取截止状态信息（基于开标时间，优先使用变更公告的开标时间）
  const getDeadlineStatus = (openBidTime: string | null, actualOpenBidTime?: string | null) => {
    const effectiveOpenBidTime = actualOpenBidTime || openBidTime;
    if (!effectiveOpenBidTime) return { status: 'normal', label: '进行中', color: '#10b981' };
    
    const now = new Date();
    const openBidDate = new Date(effectiveOpenBidTime);
    const diff = openBidDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      return { status: 'expired', label: '已截止', color: '#ef4444' };
    } else if (diff <= 3 * 24 * 60 * 60 * 1000) {
      return { status: 'urgent', label: '即将截止', color: '#f59e0b' };
    }
    return { status: 'normal', label: '进行中', color: '#10b981' };
  };

  // 获取发票状态
  const getInvoiceStatus = (bid: BidItem) => {
    if (!bid.invoice_url && !bid.expressnumber && !bid.invoice_request_time) {
      return { status: '未申请', color: '#6b7280', bg: '#f3f4f6' };
    }
    if (bid.invoice_request_time && !bid.invoice_url && !bid.expressnumber) {
      return { status: '开票中', color: '#d97706', bg: '#fffbeb' };
    }
    if (bid.invoice_url || bid.expressnumber) {
      return { status: '已开票', color: '#059669', bg: '#ecfdf5' };
    }
    return { status: '未申请', color: '#6b7280', bg: '#f3f4f6' };
  };

  // 获取发票图标
  const getInvoiceIcon = (status: string) => {
    switch (status) {
      case '已开票':
        return CheckCircle;
      case '开票中':
        return Timer;
      default:
        return Receipt;
    }
  };

  // 处理文件上传（打开弹窗）
  const handleFileUpload = (bid: BidItem) => {
    const effectiveOpenBidTime = bid.actual_open_bid_time || bid.open_bid_time;
    if (!canUpload(bid.open_bid_time, bid.allow_e_bid, bid.actual_open_bid_time)) {
      setShowTimeoutModal(true);
      return;
    }
    
    setSelectedBidForUpload(bid);
    setQuoteAmount(bid.quote_amount?.toString() || "");
    setUploadFile(null);
    setShowUploadModal(true);
  };

  // 处理投标报价变化（允许任意字符：中文/百分比等）
  const handleQuoteAmountChange = (value: string) => {
    setQuoteAmount(value);
  };

  // 确认提交上传
  const handleConfirmUpload = async () => {
    if (!selectedBidForUpload) return;
    
    // 如果是"我的投标"，需要验证报价字段
    if (announcementType === 1) {
      if (!quoteAmount) {
        showToast('请填写投标报价');
        return;
      }
    }
    
    if (!uploadFile) {
      showToast('请选择要上传的标书文件');
      return;
    }
    
    try {
      setUploading(selectedBidForUpload.order_id);
      const token = getToken();
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      // 只在"我的投标"时添加报价字段
      if (announcementType === 1) {
        formData.append('quote_amount', quoteAmount);
      }

      const response = await fetch(`/api/trade-orders/${selectedBidForUpload.order_id}/upload-bidding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      if (result.ok) {
        // 更新本地数据
        setBids((prev) =>
          prev.map((bid) =>
            bid.order_id === selectedBidForUpload.order_id
              ? { 
                  ...bid, 
                  bidding_url: result.url,
                  ...(announcementType === 1 ? {
                    // 保留原始输入（可能包含中文/百分比等），且不更新 quote_amount_upper
                    quote_amount: quoteAmount
                  } : {})
                }
              : bid
          )
        );
        setShowUploadModal(false);
        setSelectedBidForUpload(null);
        setQuoteAmount("");
        setUploadFile(null);
        showToast('上传成功');
      } else {
        showToast(result.error || '上传失败');
      }
    } catch (error) {
      console.error('上传失败:', error);
      showToast('上传失败，请重试');
    } finally {
      setUploading(null);
    }
  };

  // 下载招标文件
  const handleDownloadAnnouncementFile = async (orderId: string) => {
    try {
      const token = getToken();
      const resp = await fetch(`/api/trade-orders/${orderId}/download-announcement-file`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await resp.json();
      
      if (data.ok && data.data && data.data.downloadUrl) {
        // 触发下载
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.downloadUrl.split('/').pop() || '招标文件.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(data.error || '未找到招标文件');
      }
    } catch (err) {
      console.error('下载文件失败:', err);
      alert('下载失败，请稍后重试');
    }
  };

  // 处理文件选择并上传
  const handleFileChange = async (orderId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(orderId);
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`/api/trade-orders/${orderId}/upload-bidding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      if (result.ok) {
        // 更新本地数据
        setBids((prev) =>
          prev.map((bid) =>
            bid.order_id === orderId
              ? { ...bid, bidding_url: result.url }
              : bid
          )
        );
      } else {
        alert(result.error || '上传失败');
      }
    } catch (error) {
      console.error('上传失败:', error);
      alert('上传失败，请重试');
    } finally {
      setUploading(null);
      // 清空input以便重复上传同一文件
      if (fileInputRefs.current[orderId]) {
        fileInputRefs.current[orderId]!.value = '';
      }
    }
  };

  // 处理文件删除
  const handleFileDelete = async (orderId: string, openBidTime: string | null) => {
    if (!canUpload(openBidTime)) {
      setShowTimeoutModal(true);
      return;
    }

    if (!confirm('确定要删除投标文件吗？')) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/trade-orders/${orderId}/delete-bidding`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.ok) {
        setBids((prev) =>
          prev.map((bid) =>
            bid.order_id === orderId
              ? { ...bid, bidding_url: null }
              : bid
          )
        );
      } else {
        alert(result.error || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      alert('删除失败，请重试');
    }
  };

  // 查看PDF
  const handleViewPdf = (url: string) => {
    setCurrentPdfUrl(url);
    setShowPdfModal(true);
  };

  // 申请开票确认
  const handleRequestInvoice = (orderId: string) => {
    setSelectedOrderId(orderId);
    setShowInvoiceConfirm(true);
  };

  // 确认申请开票
  const confirmRequestInvoice = async () => {
    if (!selectedOrderId) return;

    try {
      const token = getToken();
      const response = await fetch(`/api/trade-orders/${selectedOrderId}/request-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (result.ok) {
        // 更新本地数据
        setBids((prev) =>
          prev.map((bid) =>
            bid.order_id === selectedOrderId
              ? { ...bid, invoice_request_time: new Date().toISOString() }
              : bid
          )
        );
      } else {
        alert(result.error || '申请失败');
      }
    } catch (error) {
      console.error('申请失败:', error);
      alert('申请失败，请重试');
    } finally {
      setShowInvoiceConfirm(false);
      setSelectedOrderId(null);
    }
  };

  // 下载发票
  const handleDownloadInvoice = (bid: BidItem) => {
    if (!bid.invoice_url) {
      alert('发票文件不存在');
      return;
    }
    
    // 生成文件名：项目编号_项目名称_金额.pdf
    const amount = bid.order_amount ? bid.order_amount.toFixed(2) : '0.00';
    const projectNo = bid.project_no || 'unknown';
    const projectName = bid.project_name || 'invoice';
    // 替换文件名中的非法字符
    const safeProjectName = projectName.replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `${projectNo}_${safeProjectName}_${amount}.pdf`;
    
    // 直接使用a标签下载
    const link = document.createElement('a');
    link.href = bid.invoice_url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 查看快递单号
  const handleViewExpress = (expressNumber: string) => {
    setCurrentExpressNumber(expressNumber);
    setShowExpressModal(true);
  };

  // 从URL中提取文件名
  const getFileNameFromUrl = (url: string | null) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1] || '投标文件';
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header simple />
      
      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-50px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div style={{
              width: isMobile ? '40px' : '56px',
              height: isMobile ? '40px' : '56px',
              borderRadius: '16px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FolderOpen style={{ width: isMobile ? '20px' : '28px', height: isMobile ? '20px' : '28px', color: 'white' }} />
            </div>
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              {announcementType === 1 ? '我的投标' : '其他公告报名'}
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px' }}>
            管理您已报名的投标项目，上传投标文件，跟踪项目进度
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? '12px' : '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
              <Link
                to="/"
                style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseOver={(e) => e.currentTarget.style.color = '#003c8c'}
                onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                首页
              </Link>
              <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
              <span style={{ color: '#003c8c', fontWeight: 500 }}>
                {announcementType === 1 ? '我的投标' : '其他公告报名'}
              </span>
            </div>
            <button
              onClick={() => {
                setAnnouncementType(announcementType === 1 ? 4 : 1);
                setSearchKeyword('');
              }}
              style={{
                padding: isMobile ? '6px 12px' : '8px 16px',
                backgroundColor: '#003c8c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: isMobile ? '12px' : '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#003c8c'}
            >
              {announcementType === 1 ? '切换到其他公告报名' : '切换到我的投标'}
            </button>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {/* 统计卡片 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: isMobile ? '12px' : '20px',
          marginBottom: isMobile ? '20px' : '32px'
        }}>
          {[
            { label: '全部项目', value: bids.length, icon: FolderOpen, color: '#3b82f6', bgColor: '#eff6ff' },
            { label: '进行中', value: bids.filter(b => canUpload(b.open_bid_time, b.allow_e_bid, b.actual_open_bid_time)).length, icon: Timer, color: '#10b981', bgColor: '#ecfdf5' },
            { label: '已截止', value: bids.filter(b => !canUpload(b.open_bid_time, b.allow_e_bid, b.actual_open_bid_time)).length, icon: AlertCircle, color: '#ef4444', bgColor: '#fef2f2' },
            { label: '已上传文件', value: bids.filter(b => b.bidding_url).length, icon: FileText, color: '#8b5cf6', bgColor: '#f5f3ff' }
          ].map((stat, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '16px' : '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '12px' : '20px'
            }}>
              <div style={{
                width: isMobile ? '40px' : '56px',
                height: isMobile ? '40px' : '56px',
                borderRadius: '14px',
                backgroundColor: stat.bgColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <stat.icon style={{ width: isMobile ? '20px' : '28px', height: isMobile ? '20px' : '28px', color: stat.color }} />
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 700, color: '#1f2937' }}>{stat.value}</div>
                <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280' }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 列表卡片 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* 搜索栏 */}
          <div style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(to right, #fafbfc, white)',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '12px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
              <div style={{
                width: '4px',
                height: isMobile ? '20px' : '24px',
                backgroundColor: '#003c8c',
                borderRadius: '2px'
              }} />
              <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937' }}>已报名项目</h3>
              <span style={{
                padding: isMobile ? '3px 10px' : '4px 12px',
                backgroundColor: '#eff6ff',
                color: '#003c8c',
                fontSize: isMobile ? '11px' : '13px',
                fontWeight: 500,
                borderRadius: '20px'
              }}>
                共 {filteredBids.length} 个
              </span>
            </div>
            
            <div style={{ position: 'relative', width: isMobile ? '100%' : '320px' }}>
              <input
                type="text"
                placeholder="搜索项目名称或编号..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  height: isMobile ? '40px' : '44px',
                  paddingLeft: isMobile ? '40px' : '48px',
                  paddingRight: '16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: isMobile ? '13px' : '14px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#003c8c';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(0,60,140,0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              <Search style={{ 
                position: 'absolute', 
                left: isMobile ? '12px' : '16px', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                width: isMobile ? '18px' : '20px', 
                height: isMobile ? '18px' : '20px', 
                color: '#9ca3af' 
              }} />
            </div>
          </div>

          {/* 项目列表 */}
          {loading ? (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center' }}>
              <div style={{ fontSize: isMobile ? '14px' : '16px', color: '#6b7280' }}>加载中...</div>
            </div>
          ) : filteredBids.length > 0 ? (
            <div>
              {filteredBids.map((bid, index) => {
                const deadlineStatus = getDeadlineStatus(bid.open_bid_time, bid.actual_open_bid_time);
                const invoiceStatus = getInvoiceStatus(bid);
                const InvoiceIcon = getInvoiceIcon(invoiceStatus.status);
                const canUploadFile = canUpload(bid.open_bid_time, bid.allow_e_bid, bid.actual_open_bid_time);
                const isOtherAnnouncement = announcementType === 4;
                const effectiveOpenBidTime = bid.actual_open_bid_time || bid.open_bid_time;
                
                return (
                  <div
                    key={bid.order_id}
                    onMouseEnter={() => !isMobile && setHoveredRow(bid.id)}
                    onMouseLeave={() => !isMobile && setHoveredRow(null)}
                    style={{
                      padding: isMobile ? '16px' : '24px 32px',
                      borderBottom: index < filteredBids.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: hoveredRow === bid.id && !isMobile
                        ? 'linear-gradient(to right, #eff6ff, transparent)' 
                        : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* 顶部：项目名称和状态 */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'flex-start', 
                      justifyContent: 'space-between', 
                      marginBottom: isMobile ? '16px' : '20px',
                      gap: isMobile ? '12px' : '0'
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: isMobile ? '3px 8px' : '4px 10px',
                            backgroundColor: deadlineStatus.color + '15',
                            color: deadlineStatus.color,
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 600,
                            borderRadius: '6px'
                          }}>
                            {deadlineStatus.label}
                          </span>
                          <span style={{ fontSize: isMobile ? '11px' : '13px', color: '#9ca3af', fontFamily: 'monospace' }}>
                            {bid.project_no}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                          <h4 style={{ 
                            fontSize: isMobile ? '15px' : '17px', 
                            fontWeight: 600, 
                            color: hoveredRow === bid.id && !isMobile ? '#003c8c' : '#1f2937',
                            transition: 'color 0.2s',
                            margin: 0,
                            wordBreak: 'break-word'
                          }}>
                            {bid.project_name}
                          </h4>
                          {announcementType === 1 && (
                            <button
                              onClick={() => handleDownloadAnnouncementFile(bid.order_id)}
                              style={{
                                padding: isMobile ? '5px 10px' : '6px 12px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                            >
                              <Download style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                              下载招标文件
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* 订单金额和订单号 */}
                      <div style={{ textAlign: isMobile ? 'left' : 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#9ca3af', marginBottom: '4px' }}>订单金额</div>
                        <div style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#f59e0b' }}>
                          {formatMoney(bid.order_amount)}
                        </div>
                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#9ca3af', marginTop: isMobile ? '6px' : '8px' }}>订单号</div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600, color: '#374151', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {bid.order_id}
                        </div>
                      </div>
                    </div>

                    {/* 中间：时间信息和发票状态 */}
                    {!isOtherAnnouncement ? (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
                        gap: isMobile ? '10px' : '16px',
                        marginBottom: isMobile ? '16px' : '20px'
                      }}>
                        <div style={{
                          padding: isMobile ? '10px 12px' : '14px 16px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                            <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: '#6b7280', flexShrink: 0 }} />
                            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>报名开始</span>
                          </div>
                          <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, color: '#1f2937', wordBreak: 'break-word' }}>
                            {formatDateTime(bid.file_get_start_time)}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: isMobile ? '10px 12px' : '14px 16px',
                          backgroundColor: deadlineStatus.status === 'expired' ? '#fef2f2' : deadlineStatus.status === 'urgent' ? '#fffbeb' : '#f8fafc',
                          borderRadius: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                            <AlertCircle style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: deadlineStatus.color, flexShrink: 0 }} />
                            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>报名截止</span>
                          </div>
                          <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, color: deadlineStatus.color, wordBreak: 'break-word' }}>
                            {formatDateTime(bid.file_get_end_time)}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: isMobile ? '10px 12px' : '14px 16px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                            <Clock style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: '#6b7280', flexShrink: 0 }} />
                            <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>开标时间</span>
                          </div>
                        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 500, color: '#1f2937', wordBreak: 'break-word' }}>
                          {formatDateTime(effectiveOpenBidTime)}
                        </div>
                        </div>
                        
                        <div style={{
                          padding: isMobile ? '10px 12px' : '14px 16px',
                          backgroundColor: invoiceStatus.bg,
                          borderRadius: '10px',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between',
                          gap: isMobile ? '8px' : '0',
                          gridColumn: isMobile ? 'span 2' : 'auto'
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', marginBottom: isMobile ? '4px' : '6px' }}>
                              <InvoiceIcon style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', color: invoiceStatus.color, flexShrink: 0 }} />
                              <span style={{ fontSize: isMobile ? '11px' : '12px', color: '#6b7280' }}>发票状态</span>
                            </div>
                            <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 600, color: invoiceStatus.color }}>
                              {invoiceStatus.status}
                            </div>
                          </div>
                          
                          {/* 发票操作按钮 */}
                          <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                            {invoiceStatus.status === '未申请' && (
                              <button
                                onClick={() => handleRequestInvoice(bid.order_id)}
                                style={{
                                  padding: isMobile ? '5px 10px' : '6px 12px',
                                  backgroundColor: '#003c8c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: isMobile ? '11px' : '12px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#002d6b'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#003c8c'}
                              >
                                申请开票
                              </button>
                            )}
                            {invoiceStatus.status === '已开票' && bid.invoice_url && (
                              <a
                                href={bid.invoice_url}
                                download={[bid.project_no, bid.project_name, bid.order_amount].filter(Boolean).join('_') + '.pdf'}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: isMobile ? '5px 10px' : '6px 12px',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: isMobile ? '11px' : '12px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  whiteSpace: 'nowrap',
                                  textDecoration: 'none'
                                }}
                              >
                                <Download style={{ width: isMobile ? '11px' : '12px', height: isMobile ? '11px' : '12px' }} />
                                点击下载
                              </a>
                            )}
                            {invoiceStatus.status === '已开票' && bid.expressnumber && (
                              <button
                                onClick={() => handleViewExpress(bid.expressnumber!)}
                                style={{
                                  padding: isMobile ? '5px 10px' : '6px 12px',
                                  backgroundColor: '#8b5cf6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: isMobile ? '11px' : '12px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#8b5cf6'}
                              >
                                <Package style={{ width: isMobile ? '11px' : '12px', height: isMobile ? '11px' : '12px' }} />
                                查看快递单号
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* 底部：文件操作 */}
                    <div style={{
                      display: 'flex',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      justifyContent: 'space-between',
                      padding: isMobile ? '12px' : '16px 20px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      gap: isMobile ? '12px' : '0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                        <FileText style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px', color: '#6b7280', flexShrink: 0 }} />
                        <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#6b7280' }}>投标文件上传</span>
                      </div>
                      
                      <input
                        type="file"
                        ref={(el) => (fileInputRefs.current[bid.order_id] = el)}
                        onChange={(e) => handleFileChange(bid.order_id, e)}
                        style={{ display: 'none' }}
                        accept=".pdf,.zip,.rar,.7z"
                      />
                      
                      {bid.bidding_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobile ? '6px' : '8px',
                            padding: isMobile ? '6px 12px' : '8px 16px',
                            backgroundColor: '#eff6ff',
                            borderRadius: '8px',
                            maxWidth: isMobile ? '100%' : '300px',
                            flex: isMobile ? '1 1 100%' : '0 1 auto',
                            minWidth: 0
                          }}>
                            <FileText style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px', color: '#003c8c', flexShrink: 0 }} />
                            <span style={{ 
                              fontSize: isMobile ? '12px' : '14px', 
                              color: '#003c8c', 
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0
                            }}>
                              {getFileNameFromUrl(bid.bidding_url)}
                            </span>
                          </div>
                          <button
                            onClick={() => handleViewPdf(bid.bidding_url!)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: isMobile ? '4px' : '6px',
                              padding: isMobile ? '6px 12px' : '8px 16px',
                              backgroundColor: 'white',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              color: '#6b7280',
                              fontSize: isMobile ? '12px' : '14px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Eye style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                            查看
                          </button>
                          <button
                            onClick={() => handleFileDelete(bid.order_id, effectiveOpenBidTime)}
                            disabled={!canUploadFile}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: isMobile ? '32px' : '36px',
                              height: isMobile ? '32px' : '36px',
                              backgroundColor: canUploadFile ? '#fef2f2' : '#f3f4f6',
                              border: 'none',
                              borderRadius: '8px',
                              color: canUploadFile ? '#ef4444' : '#9ca3af',
                              cursor: canUploadFile ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s',
                              flexShrink: 0
                            }}
                          >
                            <Trash2 style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '10px' : '16px', width: isMobile ? '100%' : 'auto' }}>
                          {announcementType === 1 && bid.allow_e_bid === 0 && (
                            <span style={{ 
                              fontSize: isMobile ? '11px' : '13px', 
                              color: '#ef4444',
                              whiteSpace: isMobile ? 'normal' : 'nowrap',
                              fontWeight: 500,
                              lineHeight: 1.4
                            }}>
                              该项目暂不支持电子投标，如有问题请联系甯老师，联系电话028-62073616或19980545701
                            </span>
                          )}
                          {!(announcementType === 1 && bid.allow_e_bid === 0) && (
                            <span style={{ 
                              fontSize: isMobile ? '11px' : '13px', 
                              color: '#ef4444',
                              whiteSpace: isMobile ? 'normal' : 'nowrap',
                              lineHeight: 1.4
                            }}>
                              建议上传50M以下文件，可以是压缩包文件，也可以是PDF
                            </span>
                          )}
                          <button
                            onClick={() => handleFileUpload(bid)}
                            disabled={!canUploadFile || uploading === bid.order_id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: isMobile ? '6px' : '8px',
                              padding: isMobile ? '8px 16px' : '10px 20px',
                              backgroundColor: canUploadFile ? '#003c8c' : '#e5e7eb',
                              border: 'none',
                              borderRadius: '10px',
                              color: canUploadFile ? 'white' : '#9ca3af',
                              fontSize: isMobile ? '13px' : '14px',
                              fontWeight: 500,
                              cursor: canUploadFile ? 'pointer' : 'not-allowed',
                              transition: 'all 0.2s',
                              width: isMobile ? '100%' : 'auto',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Upload style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px' }} />
                            {uploading === bid.order_id ? '上传中...' : '上传标书文件'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center' }}>
              <div style={{ 
                width: isMobile ? '80px' : '100px', 
                height: isMobile ? '80px' : '100px', 
                margin: '0 auto 24px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <FolderOpen style={{ width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', color: '#9ca3af' }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px', marginBottom: '8px' }}>暂无已报名的项目</p>
              <p style={{ color: '#9ca3af', fontSize: isMobile ? '12px' : '14px' }}>前往项目公告信息页面报名参与项目</p>
              <button
                onClick={() => navigate('/trade-announcements')}
                style={{
                  marginTop: '24px',
                  padding: isMobile ? '10px 24px' : '12px 32px',
                  backgroundColor: '#003c8c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                浏览公告
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 超时弹窗 */}
      {showTimeoutModal && (
        <>
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 50
          }} />
          <div style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: isMobile ? '16px' : '16px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '16px' : '20px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              maxWidth: '400px',
              width: '100%',
              padding: isMobile ? '24px' : '40px',
              textAlign: 'center'
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px',
                height: isMobile ? '60px' : '80px',
                margin: '0 auto 24px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle style={{ width: isMobile ? '30px' : '40px', height: isMobile ? '30px' : '40px', color: '#ef4444' }} />
              </div>
              <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
                无法操作
              </h3>
              <p style={{ color: '#6b7280', marginBottom: isMobile ? '24px' : '32px', lineHeight: 1.6, fontSize: isMobile ? '14px' : '16px' }}>
                已过开标时间，无法上传或删除投标文件！
              </p>
              <button
                onClick={() => setShowTimeoutModal(false)}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px' : '14px',
                  backgroundColor: '#003c8c',
                  color: 'white',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                我知道了
              </button>
            </div>
          </div>
        </>
      )}

      {/* PDF查看弹窗 */}
      {showPdfModal && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }} 
            onClick={() => setShowPdfModal(false)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : '90%',
            maxWidth: '1000px',
            height: isMobile ? '90vh' : '85vh',
            backgroundColor: 'white',
            borderRadius: isMobile ? '12px' : '16px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            zIndex: 51,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: isMobile ? '12px 16px' : '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f8fafc'
            }}>
              <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px', color: '#003c8c' }} />
                查看投标文件
              </h3>
              <button
                onClick={() => setShowPdfModal(false)}
                style={{
                  width: isMobile ? '32px' : '36px',
                  height: isMobile ? '32px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <X style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }} />
              </button>
            </div>
            <div style={{ flex: 1, padding: '0' }}>
              <iframe
                src={currentPdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                title="PDF Viewer"
              />
            </div>
          </div>
        </>
      )}

      {/* 申请开票确认弹窗 */}
      {showInvoiceConfirm && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }} 
            onClick={() => setShowInvoiceConfirm(false)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: isMobile ? '16px' : '20px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            maxWidth: '400px',
            width: isMobile ? '90%' : '90%',
            padding: isMobile ? '24px' : '40px',
            textAlign: 'center',
            zIndex: 51
          }}>
            <div style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              margin: '0 auto 24px',
              backgroundColor: '#eff6ff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Receipt style={{ width: isMobile ? '30px' : '40px', height: isMobile ? '30px' : '40px', color: '#003c8c' }} />
            </div>
            <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
              确认开票
            </h3>
            <p style={{ color: '#6b7280', marginBottom: isMobile ? '24px' : '32px', lineHeight: 1.6, fontSize: isMobile ? '14px' : '16px' }}>
              是否确认开票？确认后将向平台提交开票申请。
            </p>
            <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={() => setShowInvoiceConfirm(false)}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px' : '14px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                取消
              </button>
              <button
                onClick={confirmRequestInvoice}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px' : '14px',
                  backgroundColor: '#003c8c',
                  color: 'white',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer'
                }}
              >
                确认
              </button>
            </div>
          </div>
        </>
      )}

      {/* 快递单号弹窗 */}
      {showExpressModal && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }} 
            onClick={() => setShowExpressModal(false)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: isMobile ? '16px' : '20px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            maxWidth: '400px',
            width: isMobile ? '90%' : '90%',
            padding: isMobile ? '24px' : '40px',
            textAlign: 'center',
            zIndex: 51
          }}>
            <div style={{
              width: isMobile ? '60px' : '80px',
              height: isMobile ? '60px' : '80px',
              margin: '0 auto 24px',
              backgroundColor: '#f5f3ff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Package style={{ width: isMobile ? '30px' : '40px', height: isMobile ? '30px' : '40px', color: '#8b5cf6' }} />
            </div>
            <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>
              快递单号
            </h3>
            <div style={{ 
              padding: isMobile ? '12px 16px' : '16px 24px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              marginBottom: '24px',
              fontFamily: 'monospace',
              fontSize: isMobile ? '16px' : '18px',
              fontWeight: 600,
              color: '#1f2937',
              letterSpacing: '2px',
              wordBreak: 'break-all'
            }}>
              {currentExpressNumber}
            </div>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: isMobile ? '13px' : '14px' }}>
              您可以复制此单号到快递官网查询物流信息
            </p>
            <button
              onClick={() => {
                navigator.clipboard.writeText(currentExpressNumber);
                alert('快递单号已复制到剪贴板');
              }}
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '14px',
                backgroundColor: '#8b5cf6',
                color: 'white',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                marginBottom: '12px'
              }}
            >
              复制单号
            </button>
            <button
              onClick={() => setShowExpressModal(false)}
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '14px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 600,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              关闭
            </button>
          </div>
        </>
      )}

      {/* 上传标书弹窗 */}
      {showUploadModal && selectedBidForUpload && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 50
            }} 
            onClick={() => {
              setShowUploadModal(false);
              setSelectedBidForUpload(null);
              setQuoteAmount("");
              setUploadFile(null);
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: isMobile ? '16px' : '20px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            maxWidth: '600px',
            width: isMobile ? '95%' : '90%',
            padding: isMobile ? '20px' : '40px',
            zIndex: 51,
            maxHeight: isMobile ? '85vh' : '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? '16px' : '24px' }}>
              <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#1f2937' }}>上传标书文件</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedBidForUpload(null);
                  setQuoteAmount("");
                  setUploadFile(null);
                }}
                style={{
                  width: isMobile ? '32px' : '36px',
                  height: isMobile ? '32px' : '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#6b7280',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <X style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px' }} />
              </button>
            </div>

            {/* 只在"我的投标"时显示报价字段 */}
            {announcementType === 1 && (
              <>
                <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
                  <label style={{ display: 'block', fontSize: isMobile ? '13px' : '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                    投标报价<span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={quoteAmount}
                    onChange={(e) => handleQuoteAmountChange(e.target.value)}
                    placeholder="请输入投标报价"
                    style={{
                      width: '100%',
                      padding: isMobile ? '10px 14px' : '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: isMobile ? '13px' : '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#003c8c';
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(0,60,140,0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <div style={{ marginTop: '8px', fontSize: isMobile ? '11px' : '12px', color: '#ef4444' }}>
                    填写投标报价应该与投标文件中的报价保持一致
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
              <label style={{ display: 'block', fontSize: isMobile ? '13px' : '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
                标书文件<span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: isMobile ? '16px' : '24px',
                textAlign: 'center',
                backgroundColor: '#f9fafb',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.borderColor = '#003c8c';
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }
              }}
              onMouseOut={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.pdf,.zip,.rar,.7z';
                input.onchange = (e: any) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setUploadFile(file);
                  }
                };
                input.click();
              }}
              >
                {uploadFile ? (
                  <div>
                    <FileText style={{ width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', color: '#10b981', margin: '0 auto 8px' }} />
                    <p style={{ color: '#10b981', marginBottom: '4px', fontWeight: 500, fontSize: isMobile ? '13px' : '14px', wordBreak: 'break-word' }}>{uploadFile.name}</p>
                    <p style={{ color: '#6b7280', fontSize: isMobile ? '11px' : '12px' }}>点击重新选择文件</p>
                  </div>
                ) : (
                  <div>
                    <Upload style={{ width: isMobile ? '28px' : '32px', height: isMobile ? '28px' : '32px', color: '#6b7280', margin: '0 auto 8px' }} />
                    <p style={{ color: '#6b7280', marginBottom: '4px', fontSize: isMobile ? '13px' : '14px' }}>点击选择文件</p>
                    <p style={{ color: '#9ca3af', fontSize: isMobile ? '11px' : '12px' }}>支持 PDF、ZIP、RAR、7Z 格式，建议50M以下</p>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedBidForUpload(null);
                  setQuoteAmount("");
                  setUploadFile(null);
                }}
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                取消
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={
                  uploading === selectedBidForUpload.order_id || 
                  !uploadFile || 
                  (announcementType === 1 && !quoteAmount)
                }
                style={{
                  padding: isMobile ? '10px 20px' : '12px 24px',
                  backgroundColor: (
                    uploading === selectedBidForUpload.order_id || 
                    !uploadFile || 
                    (announcementType === 1 && !quoteAmount)
                  ) ? '#e5e7eb' : '#003c8c',
                  color: (
                    uploading === selectedBidForUpload.order_id || 
                    !uploadFile || 
                    (announcementType === 1 && !quoteAmount)
                  ) ? '#9ca3af' : 'white',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (
                    uploading === selectedBidForUpload.order_id || 
                    !uploadFile || 
                    (announcementType === 1 && !quoteAmount)
                  ) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {uploading === selectedBidForUpload.order_id ? '上传中...' : '确认提交'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Toast 提示 */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            zIndex: 10000,
            fontSize: '14px',
            fontWeight: 500,
            animation: 'toastSlideIn 0.3s ease-out',
            maxWidth: '90%',
            textAlign: 'center'
          }}
        >
          {toastMessage}
        </div>
      )}

      <style>{`
        @keyframes toastSlideIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}
